import type { QrDrawModel } from './model';

export interface CanvasImages {
  readonly centerImage: CanvasImageSource | null;
  readonly moduleImage: CanvasImageSource | null;
}

/**
 * Paints a draw model onto a 2D context at `devicePixelRatio` resolution.
 *
 * The canvas is resized here, which also resets the context transform — so the
 * scale is applied fresh on every paint and never compounds. All drawing below
 * happens in CSS-pixel space.
 */
export function paintToCanvas(
  canvas: HTMLCanvasElement,
  model: QrDrawModel,
  images: CanvasImages,
  pixelRatio = 1,
): void {
  const ratio = Math.max(1, pixelRatio);
  const cssSize = model.pixelSize;

  canvas.width = Math.round(cssSize * ratio);
  canvas.height = Math.round(cssSize * ratio);
  canvas.style.width = `${String(cssSize)}px`;
  canvas.style.height = `${String(cssSize)}px`;

  const ctx = canvas.getContext('2d');
  if (ctx === null) return;

  ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.fillStyle = model.background;
  ctx.fillRect(0, 0, cssSize, cssSize);

  for (const layer of model.layers) {
    ctx.fillStyle = layer.fill;
    // Nonzero is the default and is what the geometry's winding assumes.
    ctx.fill(new Path2D(layer.d));
  }

  if (images.moduleImage !== null) {
    for (const box of model.imageModules) {
      ctx.drawImage(images.moduleImage, box.x, box.y, box.size, box.size);
    }
  }

  if (model.centerImage !== null && images.centerImage !== null) {
    const { x, y, size } = model.centerImage;
    ctx.drawImage(images.centerImage, x, y, size, size);
  }
}
